import { GapCursorSelection } from '@atlaskit/editor-common/selection';
import type { EditorCommand } from '@atlaskit/editor-common/types';
import type { Transaction } from '@atlaskit/editor-prosemirror/state';
import { NodeSelection, TextSelection } from '@atlaskit/editor-prosemirror/state';
import { fg } from '@atlaskit/platform-feature-flags/fg';
import { expValEquals } from '@atlaskit/tmp-editor-statsig/exp-val-equals';

import type { OpenTypeAheadProps } from '../../types';
import { ACTIONS } from '../actions';
import { pluginKey } from '../key';

export const openTypeAhead =
	(props: OpenTypeAheadProps) =>
	(tr: Transaction): void => {
		const { triggerHandler, inputMethod, query, removePrefixTriggerOnCancel } = props;

		tr.setMeta(pluginKey, {
			action: ACTIONS.OPEN_TYPEAHEAD_AT_CURSOR,
			params: {
				triggerHandler,
				inputMethod,
				query,
				removePrefixTriggerOnCancel,
			},
		});
	};

export const openTypeAheadAtCursor =
	({
		triggerHandler,
		inputMethod,
		query,
		removePrefixTriggerOnCancel,
	}: OpenTypeAheadProps): EditorCommand =>
	({ tr }) => {
		if (triggerHandler.canOpen?.() === false && fg('editor-disable-feature')) {
			return null;
		}

		openTypeAhead({
			triggerHandler,
			inputMethod,
			query,
			removePrefixTriggerOnCancel,
		})(tr);

		const { selection } = tr;
		const isInline = selection instanceof NodeSelection && selection.node.type.isInline;

		if (
			!(
				selection instanceof TextSelection ||
				selection instanceof GapCursorSelection ||
				selection instanceof NodeSelection
			)
		) {
			return tr;
		}

		if (selection instanceof GapCursorSelection) {
			// Create space for the typeahead menu in gap cursor
			tr.insertText(' ');
			// delete 1 pos before wherever selection is now - that will delete the empty space
			tr.delete(tr.selection.from - 1, tr.selection.from);
		} else {
			if (selection instanceof NodeSelection) {
				if (isInline) {
					tr.deleteSelection();
					return tr;
				}
				return tr;
			}

			if (!selection.$cursor) {
				tr.deleteSelection();
				return tr;
			}

			// Search & Destroy placeholder
			const cursorPos = selection.$cursor.pos;
			const nodeAtCursor = tr.doc.nodeAt(cursorPos);

			const isPlaceholderAtCursorPosition =
				nodeAtCursor && nodeAtCursor.type.name === 'placeholder';

			if (nodeAtCursor && isPlaceholderAtCursorPosition) {
				tr.delete(cursorPos, cursorPos + nodeAtCursor.nodeSize);
			}

			// ME-2375 remove the superfluous '@' inserted before decoration
			// by composition (https://github.com/ProseMirror/prosemirror/issues/903)
			//
			// Update:
			// Now also handles any use case with superfluous typeahead triggers (ie. '@', ':', '/')
			// being inserted due to composition by checking if we have the trigger
			// directly before the typeahead. This should not happen unless it has
			// been eroneously added because we require whitespace/newline for typeahead.
			// Check if the text ends with the trigger character (or any character matched
			// by customRegex, to support wide-char variants like fullwidth slash ／)
			const triggerPattern =
				expValEquals('platform_editor_wide_slash_trigger', 'isEnabled', true) &&
				triggerHandler.customRegex
					? new RegExp(`(${triggerHandler.customRegex})$`, 'u')
					: null;
			const endsWithTrigger =
				selection.$head.parent.textContent.endsWith?.(triggerHandler.trigger) ||
				(triggerPattern && triggerPattern.test(selection.$head.parent.textContent));
			if (cursorPos >= 2 && !!selection?.$head?.parent?.textContent && endsWithTrigger) {
				tr.delete(cursorPos - 1, cursorPos);
			}
		}

		return tr;
	};
